import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { cloneDeep } from 'src/ep-utils';
//import { BaseState } from '../base/base-state'
import { StateHelperService } from '../../../services/proprietary/state-helper.service';
import { ContractObjectDefaults } from './contract-object.defaults';
import { AddTeamMemberPointer, RemoveTeamMemberPointer } from './contract.actions';
import {
    AddTeamMember,
    CloneTeamMember,
    DeleteTeamMember,
    RestoreTeamMember,
    UpdateTeamMember
} from './team-member.actions';

export interface TeamMemberStateModel {
    teamMember: any;
    currentID: string;
}

@State<TeamMemberStateModel>({
    name: 'teamMemberState',
    defaults: {
        teamMember: {},
        currentID: null
    }
})
@Injectable()
export class TeamMemberState {
    constructor(private helper: StateHelperService) { }

    //******* SELECTORS *********

    @Selector()
    static fullState(state: TeamMemberStateModel) {
        return state.teamMember;
    }

    @Selector()
    static currentTeamMember(state: TeamMemberStateModel) {
        if (state.teamMember[state.currentID]) {
            return state.teamMember[state.currentID];
        } else {
            return {};
        }
    }

    @Selector()
    static teamMembers(state: TeamMemberStateModel) {
        let result: any = [];
        for (var i in state.teamMember) {
            result.push(state.teamMember[i]);
        }
        return result;
    }

    @Selector()
    static teamMemberCount(state: TeamMemberStateModel) {
        let count = 0;
        for (var i in state.teamMember) {
            count++;
        }
        return count;
    }

    //******  ACTIONS  ******

    @Action(UpdateTeamMember)
    updateTeamMember(sc: StateContext<TeamMemberStateModel>, action: UpdateTeamMember) {
        const state = sc.getState();
        let partyID = action.TeamMember.Party_ID;
        const currentTeamMember = state.teamMember[partyID];
        let updateTeamMember: any = { ...action.TeamMember };
        this.helper.setItemChanged(updateTeamMember, state.teamMember[partyID].Edit_Status);
        sc.patchState({
            teamMember: {
                ...state.teamMember,
                [partyID]: {
                    ...state.teamMember[partyID],
                    ...updateTeamMember
                }
            }
        });

        //Check if updating Principal_Ind or Primary_Ind
        let principalChanged =
            this.helper.stateValueChanged(updateTeamMember.Principal_Ind, currentTeamMember.Principal_Ind) &&
            updateTeamMember.Principal_Ind == 'Y';
        let primaryChanged =
            this.helper.stateValueChanged(updateTeamMember.Primary_Ind, currentTeamMember.Primary_Ind) &&
            updateTeamMember.Primary_Ind == 'Y';
        if (principalChanged || primaryChanged) {
            let updateStateTeamMember = cloneDeep(state.teamMember);
            let roleType = updateTeamMember.Team_Member_Role;
            for (var i in updateStateTeamMember) {
                this.helper.setItemChanged(updateStateTeamMember[i], updateStateTeamMember[i].Edit_Status);

                //If Team Member record's Principal_Ind is changed to "Y", Set to "N" for all other Team Member Records of the same role type.
                if (principalChanged) {
                    if (updateStateTeamMember[i].Party_ID == partyID) {
                        updateStateTeamMember[i].Principal_Ind = 'Y';
                    } else {
                        if (updateStateTeamMember[i].Team_Member_Role == roleType) {
                            updateStateTeamMember[i].Principal_Ind = 'N';
                        }
                    }
                }

                //If Team Member record's Primary is changed to "Y", Set to "N" for all other Team Member Records.
                if (primaryChanged) {
                    if (updateStateTeamMember[i].Party_ID == partyID) {
                        updateStateTeamMember[i].Primary_Ind = 'Y';
                    } else {
                        updateStateTeamMember[i].Primary_Ind = 'N';
                    }
                }
            }

            sc.patchState({ teamMember: updateStateTeamMember });
        }
    }

    @Action(DeleteTeamMember)
    deleteTeamMember(sc: StateContext<TeamMemberStateModel>, action: DeleteTeamMember) {
        const state = sc.getState();
        let teamMember = { ...state.teamMember }; //shallow copy TeamMember from state
        this.helper.setItemDeleted(teamMember, action.partyID);
        sc.patchState({ teamMember: teamMember });
        if (action.partyID < 0) {
            sc.dispatch(new RemoveTeamMemberPointer(action.partyID.toString()));
        }
    }

    @Action(RestoreTeamMember)
    restoreTeamMember(sc: StateContext<TeamMemberStateModel>, action: RestoreTeamMember) {
        const state = sc.getState();
        let teamMember = { ...state.teamMember }; //shallow copy TeamMember from state
        this.helper.setItemRestored(teamMember, action.partyID);
        sc.patchState({ teamMember: teamMember });
    }

    @Action(CloneTeamMember)
    cloneTeamMember(sc: StateContext<TeamMemberStateModel>, action: CloneTeamMember) {
        const state = sc.getState();
        let srcID = action.partyID;
        let newID = this.helper.getTempID();
        let newTeamMember = cloneDeep(state.teamMember[srcID]);

        //If the record being cloned was Principal, set the clone's Principal_Ind = "N"
        if (newTeamMember.Principal_Ind == 'Y') {
            newTeamMember.Principal_Ind = 'N';
        }

        //If the record being cloned was Primary, set the clone's Primary_Ind = "N"
        if (newTeamMember.Primary_Ind == 'Y') {
            newTeamMember.Primary_Ind = 'N';
        }

        this.helper.setItemAdded(newTeamMember);
        newTeamMember.Party_ID = newID;
        sc.patchState({
            currentID: newID,
            teamMember: { ...state.teamMember, [newID]: newTeamMember }
        });
        sc.dispatch(new AddTeamMemberPointer(newID));
    }

    @Action(AddTeamMember)
    addTeamMember(sc: StateContext<TeamMemberStateModel>, action: AddTeamMember) {
        const state = sc.getState();
        let newID = this.helper.getTempID();
        let duplicateTeamMember: boolean = false;

        if (
            action.TeamMember &&
            action.TeamMember.Employee &&
            action.TeamMember.Employee.Entity_ID &&
            action.TeamMember.Team_Member_Role
        ) {
            for (var i in state.teamMember) {
                if (
                    state.teamMember[i].Employee.Entity_ID == action.TeamMember.Employee.Entity_ID &&
                    state.teamMember[i].Team_Member_Role == action.TeamMember.Team_Member_Role
                ) {
                    duplicateTeamMember = true;
                    break;
                }
            }
        }

        if (!duplicateTeamMember) {
            let newTeamMember = this.helper.createDefaultItem(ContractObjectDefaults, 'Team_Member', action.TeamMember);

            for (var i in state.teamMember) {
                if (state.teamMember[i].Primary_Ind == 'Y') {
                    newTeamMember.Primary_Ind = 'N';
                    break;
                }
            }

            for (var i in state.teamMember) {
                if (
                    state.teamMember[i].Team_Member_Role == newTeamMember.Team_Member_Role &&
                    state.teamMember[i].Principal_Ind == 'Y'
                ) {
                    newTeamMember.Principal_Ind = 'N';
                    break;
                }
            }

            this.helper.setItemAdded(newTeamMember);
            newTeamMember.Party_ID = newID;
            sc.patchState({
                currentID: newID,
                teamMember: { ...state.teamMember, [newID]: newTeamMember }
            });
            sc.dispatch(new AddTeamMemberPointer(newID));
        }
    }
}
